import monthViewTemplate from './calendar-month-template.html';
import monthCellViewTemplate from './calendar-month-cell-template.html';
import monthCellEventsTemplate from './calendar-month-cell-events-template.html';
import calendarDayViewTemplate from './calendar-day-view-template.html';
import calendarWeekViewTemplate from './calendar-week-view-template.html';
import calendarSlideBoxTemplate from './calendar-slide-box-template.html';
import { createError } from '../../../../../lib/error';

class Calendar {

  constructor(
    moment,
    $templateCache,
    $scope,
    $q,
    showAvailabilityModal,
    StaffUserService,
    DateTimeService,
    DataSyncService,
    PositionService,
    LogService
  ) {
    "ngInject";
    this.showAvailabilityModal = showAvailabilityModal;
    this.StaffUserService = StaffUserService;
    this.DateTimeService = DateTimeService;
    this.DataSyncService = DataSyncService;
    this.PositionService = PositionService;
    this.LogService = LogService;

    this.moment = moment;
    this.$scope = $scope;

    this._currentDate = moment().toDate();
    this.calendarView = 'month';
    this.events = [];

    this._clickedDate = null;

    $templateCache.put('calendar-month-template.html', monthViewTemplate);
    $templateCache.put('calendar-month-cell-template.html', monthCellViewTemplate);
    $templateCache.put('calendar-month-cell-events-template.html', monthCellEventsTemplate);
    $templateCache.put('calendar-day-view-template.html', calendarDayViewTemplate);
    $templateCache.put('calendar-week-view-template.html', calendarWeekViewTemplate);
    $templateCache.put('calendar-slide-box-template.html', calendarSlideBoxTemplate);

    $scope.$on('calendarClickEditEvent', (e, event) => {
      this.editEventClicked(event);
    });
  };

  $onInit() {
    const positionsChanged = () => {
      this.events = this.convertPositionsToEvents(this.staffUser.positions);
    };
    this.$scope.$on('StaffUserService.refreshPositionsComplete', positionsChanged);
    positionsChanged();

    // We listen for badgeClick only so we can prevent dayClick from running when the badge is tapped.
    // Otherwise, since the badge is a child of the day, day will fire.
    this.$scope.$on('badgeClick', (event, data) => {
      this._clickedDate = data.date.format('YYYY-MM-DD');
    });

    this.$scope.$on('dayClick', (event, data) => {
      if (!this.isOnline) {
        return;
      }

      // Check if the badge on this date just got clicked. Stop execution if it has.
      if (this._clickedDate === data.date.format('YYYY-MM-DD')) {
        this._clickedDate = null;
        return;
      }

      this.showAvailabilityModal({
        date: data.date.format('MMM D, YYYY')
      })
        .then(result => {
          this.StaffUserService.setAvailability(data.date.format('YYYY-MM-DD'), result)
            .then(() => {
              this.StaffUserService.refreshData(this.staffUser).then (() => {
                // A hack - we need to refresh the current view of the calendar so that the cell modifier (i.e.
                // the availability colouring) renders and there's no refresh function built in.
                this.nextPeriod();
                this.prevPeriod();
              });
            });
        })
    });
  }

  switchView( view ) {
    this.calendarView = view;
  }

  get currentDate() {
    return this._currentDate;
  }
  set currentDate(date) {
    this._currentDate = date;
  }


  /*
  This is a modifier executed by the calendar module that passes each calendar cell before rendering.
  The cell object can be modified for each date here.
   */
  cellAvailableModifier(cell) {
    let dateString = (this.moment(cell.date).format('YYYY-MM-DD'));

    if (this.staffUser.unavailableDates.indexOf(dateString) > -1) {
      cell.cssClass = 'unavailable';
    } else {
      cell.cssClass = 'available';
    }
  }

  convertPositionsToEvents( positions ) {
    const events = [];
    for (const position of positions) {
      const eventTimezoneName = position.event && position.event.timezoneName;
      const startTimeLocal = this.DateTimeService.parseDateWithTimezoneOrServerTimezone(position.start_time, eventTimezoneName);
      const endTimeLocal = this.DateTimeService.parseDateWithTimezoneOrServerTimezone(position.end_time, eventTimezoneName);

      const startTimeShifted = this.DateTimeService.shiftDateObjectFromLocal(startTimeLocal, eventTimezoneName);
      const endTimeShifted = this.DateTimeService.shiftDateObjectFromLocal(endTimeLocal, eventTimezoneName);
      events.push({
        'title' : position.venue_name,
        'address' : position.street_address1 + ', ' + position.city + ', ' + position.state,
        'startsAt' : startTimeShifted,
        'endsAt' : endTimeShifted,
        'allDay' : false,
         color: { // can also be calendarConfig.colorTypes.warning for shortcuts to the deprecated event types
          primary: '#e3bc08', // the primary event color (should be darker than secondary)
          secondary: '#fdf1ba' // the secondary event color (should be lighter than primary)
        },
        // Include reference to original position object
        positionInfo: {
          id: position.id,
          canEdit: position.canEdit,
        },
      });
    }
    return events;
  }

  resetCalendarDate() {
    this._currentDate = this.moment().toDate();
  };

  prevPeriod() {
    this._currentDate = this.moment(this._currentDate).subtract(1, this.calendarView);
  }

  nextPeriod() {
    this._currentDate = this.moment(this._currentDate).add(1, this.calendarView);
  }

  eventClicked(calendarEvent) {
  }

  editEventClicked(event) {
    const positionId = event.positionInfo.id;
    return this.PositionService.getLocalById(positionId)
      .then(position => {
        this.$scope.$emit('showEditPosition', position);
      })
      .catch(err => {
        console.error("Calendar.editEventClicked() Got error doing position lookup:", err);
        return this.$q.reject(createError({
          error: 'ErrorFindingPositionByIdForEdit',
          message: `Couldn't find position with id ${positionId}`,
        }));
      });
  }
}

export default Calendar;

